import { CachedUserAvatar } from '@affine/core/components/cached-user-avatar';
import {
  type AffineUserInfo,
  isExistedUserInfo,
  type UserListService,
  type UserService,
} from '@blocksuite/affine/shared/services';
import { css } from '@emotion/css';

import { useSignalValue } from '../../../../modules/doc-info/utils';
import { useMemberInfo } from '../../hooks/use-member-info';

/**
 * `WriterInfoProvider` payload shape consumed by Created By /
 * Updated By cells. Member cells don't feed a writer info (their
 * ids go straight through the user services), so the prop is
 * optional on {@link UserAvatarLabel}.
 */
export interface UserAvatarWriterInfo {
  readonly id: string;
  readonly name?: string | null;
  readonly avatar?: string | null;
}

const previewRowStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  overflow: 'visible',
  minWidth: 0,
  // `flex-basis: 0` forces this row to start from zero width inside
  // the flex-wrap cell container and grow to fill its share. Without
  // an explicit basis, flex items fall back to their intrinsic size
  // (avatar + full name) and blow past the cell edge before the
  // inner `text-overflow: ellipsis` on the name has a chance to run.
  // With `flex-grow: 1` a single-assignee row spans the full cell;
  // multiple assignees split the cell proportionally, and each name
  // truncates independently once its share is smaller than its text.
  flex: '1 1 0',
});

const nameStyle = css({
  // `min-width: 0` overrides the default `min-width: auto` on flex
  // items (which is content's intrinsic size). Without it, the name
  // column would never shrink below its natural width and the
  // ellipsis would never kick in — the row would just push past
  // the cell edge instead.
  flex: '1 1 0',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '14px',
  lineHeight: '22px',
});

const avatarStyle = css({
  flexShrink: 0,
});

const normalizeAvatarUrl = (
  url: string | null | undefined
): string | undefined => {
  if (!url) return undefined;
  const trimmed = url.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const getUserName = (
  user: AffineUserInfo | null | undefined
): string | null => {
  if (!user) return null;
  return isExistedUserInfo(user) ? (user.name ?? null) : null;
};

const getUserAvatar = (
  user: AffineUserInfo | null | undefined
): string | null => {
  if (!user) return null;
  return isExistedUserInfo(user) ? (user.avatar ?? null) : null;
};

/**
 * Single avatar + name row used by every user-info cell in the
 * database block (Member, Created By, Updated By). Resolves the
 * display name from up to three sources — the primary user service,
 * the user-list service, and the optional writer info — falling
 * back to the raw member id if none of them yields a name.
 */
export const UserAvatarLabel = ({
  memberId,
  userService,
  userListService,
  writerInfo,
}: {
  memberId: string;
  userService: UserService | null | undefined;
  userListService: UserListService | null | undefined;
  writerInfo?: UserAvatarWriterInfo | null;
}) => {
  const userInfo = useMemberInfo(memberId, userService);
  const userList = useSignalValue(userListService?.users$);
  const listUser = userList?.find(user => user.id === memberId);
  const writerUser =
    writerInfo && writerInfo.id === memberId ? writerInfo : null;
  const isRemoved = userInfo?.removed;
  const name = isRemoved
    ? 'Deleted user'
    : (getUserName(userInfo) ??
      getUserName(listUser) ??
      writerUser?.name ??
      memberId);
  const avatarUrl = isRemoved
    ? undefined
    : normalizeAvatarUrl(
        getUserAvatar(userInfo) ?? getUserAvatar(listUser) ?? writerUser?.avatar
      );
  return (
    <div className={previewRowStyle}>
      <CachedUserAvatar
        userId={memberId}
        name={name}
        className={avatarStyle}
        url={avatarUrl}
        size={24}
      />
      <div className={nameStyle}>{name}</div>
    </div>
  );
};
